import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "@/app/hooks";
import { fetchApplicationById } from "@/features/applicant/applicationSlice";
import apiClient from "@/lib/api/apiClient";
import { showApiError, showError, showInfo } from "@/lib/utils/notifications";

export type VerifastConfig = {
  VERIFAST_ENABLED?: string;
  VERIFAST_ID_ONLY?: string;
  VERIFAST_APPLICANT_HOST?: string;
};

export type VerifastStatus = {
  id_verify_status?: string | null;
  completion_status?: string | null;
  approval_status?: string | null;
  error_message?: string | null;
  session_id?: string | null;
};

function isFlagOn(value?: string | null) {
  return ["1", "true", "yes", "on"].includes(
    String(value || "").toLowerCase().trim(),
  );
}

function isVerifastIdentityFailure(status: VerifastStatus | null | undefined): boolean {
  const rawStatus = String(status?.id_verify_status || "").toLowerCase();
  if (rawStatus === "failed") return true;

  const error = String(status?.error_message || "").toLowerCase();
  return (
    error.includes("verification.identity.failed") ||
    error.includes("session.rejected") ||
    error.includes("session.expired")
  );
}

export function useVerifastIdentity(
  applicantId?: string | number | null,
  isReadOnly = false,
) {
  const dispatch = useAppDispatch();
  const [config, setConfig] = useState<VerifastConfig | null>(null);
  const [status, setStatus] = useState<VerifastStatus | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [statusReady, setStatusReady] = useState(false);
  const notifyCompleteRef = useRef(false);

  const enabled = isFlagOn(config?.VERIFAST_ENABLED);
  const rawStatus = String(status?.id_verify_status || "pending").toLowerCase();
  const identityFailed = isVerifastIdentityFailure(status);
  const isComplete = enabled && statusReady && rawStatus === "complete";
  const isFailed = enabled && statusReady && identityFailed;
  const isNeedsReview = enabled && statusReady && !identityFailed && rawStatus === "needs_review";
  const isInProgress =
    enabled && statusReady && !identityFailed && rawStatus === "in_progress";
  const canStart =
    enabled &&
    statusReady &&
    !isComplete &&
    !isFailed &&
    !isNeedsReview &&
    !isReadOnly &&
    !showPopup;

  const refreshStatus = useCallback(async (): Promise<VerifastStatus | null> => {
    if (!applicantId) return null;

    const response = await apiClient.get<VerifastStatus>(
      `/api/verifast/status/${applicantId}`,
    );
    setStatus(response.data);
    setStatusReady(true);

    const idStatus = String(response.data?.id_verify_status || "").toLowerCase();
    if (idStatus === "complete") {
      await dispatch(fetchApplicationById(Number(applicantId))).unwrap();
      if (notifyCompleteRef.current) {
        notifyCompleteRef.current = false;
        showInfo("ID verification was successful. You may continue.");
      }
    } else if (
      idStatus === "needs_review" ||
      idStatus === "failed" ||
      isVerifastIdentityFailure(response.data)
    ) {
      notifyCompleteRef.current = false;
    }

    return response.data;
  }, [applicantId, dispatch]);

  const pollStatus = useCallback(async () => {
    for (let attempt = 0; attempt < 6; attempt += 1) {
      await new Promise((resolve) =>
        window.setTimeout(resolve, attempt === 0 ? 0 : 10000),
      );
      const next = await refreshStatus();
      const idStatus = String(next?.id_verify_status || "").toLowerCase();
      if (
        ["complete", "failed", "needs_review"].includes(idStatus) ||
        isVerifastIdentityFailure(next)
      ) {
        return;
      }
    }
  }, [refreshStatus]);

  const handlePopupClose = useCallback(
    (reason: "manual" | "complete") => {
      setShowPopup(false);
      setIframeUrl(null);
      notifyCompleteRef.current = true;
      showInfo(
        reason === "complete"
          ? "Identity verification submitted. Checking results..."
          : "Verification window closed. Checking results...",
      );
      void pollStatus();
    },
    [pollStatus],
  );

  const handleStepComplete = useCallback(() => {
    notifyCompleteRef.current = true;
    void refreshStatus();
  }, [refreshStatus]);

  const startVerification = useCallback(async () => {
    if (!applicantId || isReadOnly) return;

    try {
      setStarting(true);
      setIframeUrl(null);
      setShowPopup(false);

      const sessionResponse = await apiClient.post<{
        session_id?: string;
        status?: string;
        iframe_url?: string;
      }>(`/api/verifast/create_session/${applicantId}`, {});

      const url = sessionResponse.data?.iframe_url || null;
      if (sessionResponse.data?.status) {
        setStatus((prev) => ({
          ...(prev || {}),
          id_verify_status: sessionResponse.data.status || "pending",
          session_id: sessionResponse.data.session_id || prev?.session_id,
        }));
      }

      if (!url) {
        showError("Unable to load identity verification. Please try again.");
        return;
      }

      setIframeUrl(url);
      setShowPopup(true);
    } catch (error) {
      setShowPopup(false);
      setIframeUrl(null);
      showApiError(error);
    } finally {
      setStarting(false);
    }
  }, [applicantId, isReadOnly]);

  const refreshStatusRef = useRef(refreshStatus);
  refreshStatusRef.current = refreshStatus;

  useEffect(() => {
    setShowPopup(false);
    setIframeUrl(null);
  }, [applicantId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!applicantId) return;
      try {
        setConfigLoading(true);
        setStatusReady(false);
        const configResponse = await apiClient.get<VerifastConfig>(
          "/api/config/verifast",
        );
        if (cancelled) return;
        const nextConfig = configResponse.data || {};
        setConfig(nextConfig);
        if (isFlagOn(nextConfig.VERIFAST_ENABLED)) {
          try {
            await refreshStatusRef.current();
          } catch {
            if (!cancelled) {
              setStatus({ id_verify_status: "pending" });
              setStatusReady(true);
            }
          }
        } else {
          setStatusReady(true);
        }
      } catch (error) {
        if (!cancelled) {
          setStatusReady(true);
          showApiError(error);
        }
      } finally {
        if (!cancelled) {
          setConfigLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [applicantId]);

  return {
    enabled,
    statusReady,
    rawStatus,
    isComplete,
    isFailed,
    isNeedsReview,
    isInProgress,
    canStart,
    config,
    status,
    iframeUrl,
    showPopup,
    configLoading,
    starting,
    setStatus,
    startVerification,
    handlePopupClose,
    handleStepComplete,
  };
}
